/**
 * AgentQuickActionsBar Component
 * Horizontal scrollable bar of quick action buttons
 */

import React, { useCallback } from 'react'
import {
  FileText,
  Sparkles,
  CheckSquare,
  Languages,
  Palette,
  BarChart2,
  Mail,
  Lightbulb,
  Wand2,
  type LucideIcon,
} from 'lucide-react'
import type { AgentQuickAction } from '../../types'

interface AgentQuickActionsBarProps {
  /**
   * List of quick actions to display
   */
  actions: AgentQuickAction[]
  /**
   * Callback when an action is clicked
   */
  onActionClick: (action: AgentQuickAction) => void
  /**
   * Whether actions are disabled
   */
  disabled?: boolean
}

/**
 * Map icon names to Lucide components
 */
const iconMap: Record<string, LucideIcon> = {
  'file-text': FileText,
  sparkles: Sparkles,
  'check-square': CheckSquare,
  languages: Languages,
  palette: Palette,
  'bar-chart-2': BarChart2,
  mail: Mail,
  lightbulb: Lightbulb,
  wand2: Wand2,
}

/**
 * Get icon component by name
 */
function getIconComponent(iconName: string): LucideIcon {
  return iconMap[iconName] || Sparkles
}

/**
 * Horizontal scrollable bar of quick action buttons
 */
export const AgentQuickActionsBar: React.FC<AgentQuickActionsBarProps> = ({
  actions,
  onActionClick,
  disabled = false,
}) => {
  const handleClick = useCallback(
    (action: AgentQuickAction) => {
      if (!disabled) {
        onActionClick(action)
      }
    },
    [disabled, onActionClick]
  )

  if (actions.length === 0) {
    return null
  }

  return (
    <div className="border-b border-gray-100 bg-gray-50/50 px-4 py-2">
      <div
        className="flex gap-2 overflow-x-auto scrollbar-hide"
        role="toolbar"
        aria-label="Quick actions"
      >
        {actions.map((action) => {
          const Icon = getIconComponent(action.icon)

          return (
            <button
              key={action.id}
              type="button"
              onClick={() => handleClick(action)}
              disabled={disabled}
              className="
                inline-flex flex-shrink-0 items-center gap-1.5 rounded-full
                border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium
                text-gray-700 transition-all
                hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700
                disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-400
              "
              title={action.label}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap">{action.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default AgentQuickActionsBar
